import { useMemo, useState } from 'react';
import { Copy, Check, Share2 } from 'lucide-react';
import { Button, Card, CardContent, Input } from '@jeraisonne/ui';
import { QrCodeImage } from '@/components/qr-code';
import { useEnsurePeerInviteCode, useAcceptPeerInvite } from '@/lib/student/peers';

interface Props {
  studentId: string;
}

/**
 * Carte "Inviter un camarade" : affiche le code permanent de l'élève, un QR
 * code et un bouton "Copier le lien". Permet aussi de saisir le code d'un
 * autre élève pour l'ajouter en camarade.
 */
export function PeerInviteCard({ studentId }: Props) {
  const { data: code, isLoading } = useEnsurePeerInviteCode(studentId);
  const accept = useAcceptPeerInvite(studentId);
  const [copied, setCopied] = useState(false);
  const [inputCode, setInputCode] = useState('');
  const [success, setSuccess] = useState<string | null>(null);

  const shareUrl = useMemo(() => {
    if (!code) return '';
    return `${window.location.origin}/student/contacts/accept-peer/${code}`;
  }, [code]);

  async function handleCopy() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore — l'utilisateur peut toujours copier manuellement
    }
  }

  async function handleShare() {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Ajoute-moi sur Jeraisonne',
          text: 'Clique sur le lien pour me rejoindre en camarade :',
          url: shareUrl,
        });
      } catch {
        // user cancelled
      }
    } else {
      handleCopy();
    }
  }

  function handleAccept(e: React.FormEvent) {
    e.preventDefault();
    const c = inputCode.trim();
    if (!c) return;
    accept.mutate(c, {
      onSuccess: (res) => {
        setSuccess(res.peer_display_name);
        setInputCode('');
        setTimeout(() => setSuccess(null), 4000);
      },
    });
  }

  return (
    <Card className="border-primary-200 bg-primary-50/40">
      <CardContent className="space-y-4 py-4">
        <div>
          <p className="text-ink-900 text-sm font-semibold">Mon code de camarade</p>
          <p className="text-ink-500 mt-0.5 text-xs">
            Partage ce code (ou le QR) avec un camarade pour qu'il t'ajoute.
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="border-ink-100 rounded-xl border bg-white p-2">
            {code ? (
              <QrCodeImage value={shareUrl} size={120} />
            ) : (
              <div className="bg-ink-100 h-[120px] w-[120px] animate-pulse rounded" />
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <p
              className="font-display text-primary-700 select-all break-all text-xl font-extrabold tracking-widest"
              aria-label="Code d'invitation"
            >
              {isLoading ? '…' : code}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopy}
                disabled={!code}
                className="gap-1.5"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copié' : 'Copier le lien'}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleShare}
                disabled={!code}
                className="gap-1.5"
              >
                <Share2 className="h-3.5 w-3.5" />
                Partager
              </Button>
            </div>
          </div>
        </div>

        <div className="border-ink-100 border-t pt-3">
          <p className="text-ink-700 mb-2 text-xs font-semibold">
            Ou entre le code d'un camarade :
          </p>
          <form onSubmit={handleAccept} className="flex gap-2">
            <Input
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              placeholder="ABCDEFGH"
              maxLength={8}
              className="font-mono uppercase tracking-widest"
              disabled={accept.isPending}
            />
            <Button type="submit" disabled={!inputCode.trim() || accept.isPending} size="sm">
              {accept.isPending ? '…' : <Check className="h-4 w-4" />}
            </Button>
          </form>
          {accept.isError && (
            <p className="text-accent-600 mt-2 text-xs">{(accept.error as Error).message}</p>
          )}
          {success && (
            <p className="mt-2 text-xs font-medium text-emerald-600">
              {success} est maintenant ton camarade !
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
